import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Progress } from 'components/ui/progress';
import { FiPrinter, FiRefreshCw, FiAlertTriangle } from 'react-icons/fi';

interface PrinterStatus {
  name: string;
  status: 'online' | 'offline' | 'printing' | 'error' | 'paper-jam' | 'low-toner';
  currentJob?: {
    id: number;
    name: string;
    progress: number;
    pagesCompleted: number;
    totalPages: number;
  };
  paperLevels: {
    trayA4: number;
    trayA3: number;
  };
  tonerLevels: {
    black: number;
    cyan: number;
    magenta: number;
    yellow: number;
  };
  lastUpdated: Date;
}

const PrinterStatusMonitor: React.FC = () => {
  const [status, setStatus] = useState<PrinterStatus>({
    name: 'Ricoh IM-C4500',
    status: 'offline',
    paperLevels: { trayA4: 85, trayA3: 60 },
    tonerLevels: { black: 45, cyan: 78, magenta: 82, yellow: 71 },
    lastUpdated: new Date()
  });
  
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Simulate real-time updates (in production, this would poll the printer API)
  useEffect(() => {
    const interval = setInterval(() => {
      // Simulate status changes
      setStatus(prev => ({
        ...prev,
        lastUpdated: new Date(),
        // Simulate random status updates
        paperLevels: {
          trayA4: Math.max(0, prev.paperLevels.trayA4 - Math.random() * 2),
          trayA3: Math.max(0, prev.paperLevels.trayA3 - Math.random() * 1)
        },
        tonerLevels: {
          black: Math.max(0, prev.tonerLevels.black - Math.random() * 0.5),
          cyan: Math.max(0, prev.tonerLevels.cyan - Math.random() * 0.3),
          magenta: Math.max(0, prev.tonerLevels.magenta - Math.random() * 0.3),
          yellow: Math.max(0, prev.tonerLevels.yellow - Math.random() * 0.3)
        }
      }));
    }, 10000); // Update every 10 seconds

    return () => clearInterval(interval);
  }, []);

  const refreshStatus = async () => {
    setIsRefreshing(true);
    
    try {
      // In production, this would call the printer status API
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setStatus(prev => ({
        ...prev,
        status: 'online',
        lastUpdated: new Date()
      }));
    } catch (error) {
      setStatus(prev => ({ ...prev, status: 'error' }));
    } finally {
      setIsRefreshing(false);
    }
  };

  const getStatusColor = (status: PrinterStatus['status']) => {
    switch (status) {
      case 'online': return 'bg-green-100 text-green-800';
      case 'printing': return 'bg-blue-100 text-blue-800';
      case 'offline': return 'bg-gray-100 text-gray-800';
      case 'error':
      case 'paper-jam':
      case 'low-toner': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: PrinterStatus['status']) => {
    const labels = {
      'online': 'En ligne',
      'offline': 'Hors ligne',
      'printing': 'Impression',
      'error': 'Erreur',
      'paper-jam': 'Bourrage papier',
      'low-toner': 'Toner faible'
    };
    return labels[status];
  };

  const getLevelColor = (level: number) => {
    if (level > 50) return 'bg-green-500';
    if (level > 20) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="space-y-4">
      {/* Main Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FiPrinter />
              <span>{status.name}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Badge className={getStatusColor(status.status)}>
                {getStatusText(status.status)}
              </Badge>
              <Button
                onClick={refreshStatus}
                disabled={isRefreshing}
                size="sm"
                variant="outline"
              >
                <FiRefreshCw className={isRefreshing ? 'animate-spin' : ''} />
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground">
            Dernière mise à jour: {status.lastUpdated.toLocaleTimeString()}
          </p>
        </CardContent>
      </Card>

      {/* Current Job */}
      {status.currentJob && (
        <Card>
          <CardHeader>
            <CardTitle>Travail en cours</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <p className="font-medium">{status.currentJob.name}</p>
                <p className="text-sm text-muted-foreground">Job #{status.currentJob.id}</p>
              </div>
              <Progress value={status.currentJob.progress} />
              <p className="text-xs">
                {status.currentJob.pagesCompleted} / {status.currentJob.totalPages} pages
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Paper Levels */}
      <Card>
        <CardHeader>
          <CardTitle>Niveaux de papier</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-sm">
                <span>Bac A4</span>
                <span>{Math.round(status.paperLevels.trayA4)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${getLevelColor(status.paperLevels.trayA4)}`}
                  style={{ width: `${status.paperLevels.trayA4}%` }}
                />
              </div>
            </div>
            
            <div>
              <div className="flex justify-between text-sm">
                <span>Bac A3</span>
                <span>{Math.round(status.paperLevels.trayA3)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${getLevelColor(status.paperLevels.trayA3)}`}
                  style={{ width: `${status.paperLevels.trayA3}%` }}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Toner Levels */}
      <Card>
        <CardHeader>
          <CardTitle>Niveaux de toner</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(status.tonerLevels).map(([color, level]) => (
              <div key={color}>
                <div className="flex justify-between text-sm">
                  <span className="capitalize">{color}</span>
                  <span>{Math.round(level)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${getLevelColor(level)}`}
                    style={{ width: `${level}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
          
          {Object.values(status.tonerLevels).some(level => level < 20) && (
            <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded flex items-center space-x-2">
              <FiAlertTriangle className="text-yellow-600" />
              <span className="text-sm text-yellow-800">Toner faible détecté</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PrinterStatusMonitor;